import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import api from "../../apiWithAccessToken";
import { Chart } from "chart.js";

// ------------------- API PDF Download (New) -------------------
export async function downloadResponseAsPDF(responseId, filename = "report.pdf") {
  if (!responseId) {
    console.error("No response ID provided for PDF download.");
    alert("Cannot download: Missing Response ID.");
    return;
  }

  try {
    document.body.style.cursor = "wait"; 

    const response = await api.post(
      "/export/pdf", 
      { response_id: responseId },
      { responseType: "blob" }
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none"; // ✅ ADDED: Prevent visual layout shift / scroll jump
    document.body.appendChild(a);
    a.click();
    
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

  } catch (error) {
    console.error("API PDF Download Error:", error);
    if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
            const errorJson = JSON.parse(errorText);
            alert("Failed to download: " + (errorJson.error || error.message));
        } catch (e) {
            alert("Failed to download PDF: Server Error");
        }
    } else {
        alert("Failed to download PDF: " + (error.message || "Unknown Error"));
    }
  } finally {
    document.body.style.cursor = "default";
  }
}

// ... [Keep downloadBubbleAsPDF, downloadCanvasAsImage, downloadChartAsHTML, attachChartDownloadDropdown exactly as they were] ...
// ------------------- Client-Side Fallback (Old) -------------------
export async function downloadBubbleAsPDF(contentEl, { filename = "Geneilytics_response.pdf", title = "Geneilytics:" } = {}) {
  if (!contentEl) return;

  try {
    /*Off-screen stage for clean rendering*/
    const stage = document.createElement("div");
    stage.style.position = "fixed";
    stage.style.left = "-10000px";
    stage.style.top = "0";
    stage.style.width = "800px";
    stage.style.padding = "24px";
    stage.style.background = "#ffffff";
    stage.style.color = "#111827";
    stage.style.font = "14px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";
    stage.style.lineHeight = "1.55";
    stage.style.zIndex = "-1000";
    document.body.appendChild(stage);

    /* Header + timestamp*/
    const head = document.createElement("div");
    head.style.marginBottom = "12px";
    const ts = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
    head.innerHTML = `
      <div style="font-weight:700;font-size:20px;margin-bottom:4px;text-align:center;">${title}</div>
      <div style="font-size:12px;color:#6b7280;text-align:center;">Generated on ${ts} (IST)</div>
    `;
    stage.appendChild(head);

    // Panel for bubble content
    const panel = document.createElement("div");
    panel.style.border = "1px solid #e5e7eb";
    panel.style.borderRadius = "10px";
    panel.style.padding = "16px";
    panel.style.background = "#fff";
    stage.appendChild(panel);

    // Clone content and remove UI elements
    const clone = contentEl.cloneNode(true);
    clone.querySelectorAll([
      ".chart-actions", ".dl-trigger", ".dl-menu",
      ".download-pdf-btn", ".download-btn", "button[title='Download']",
      ".overlay", ".modal", ".popup", ".tooltip", ".dropdown-menu", ".context-menu"
    ].join(",")).forEach(n => n.remove());

    // Replace canvases with images
    const origCanvases = contentEl.querySelectorAll("canvas");
    const cloneCanvases = clone.querySelectorAll("canvas");
    cloneCanvases.forEach((cv, i) => {
      const srcCanvas = origCanvases[i];
      if (!srcCanvas) return;
      try {
        const img = new Image();
        img.src = srcCanvas.toDataURL("image/png");
        img.style.maxWidth = "100%";
        img.style.borderRadius = "10px";
        img.style.border = "1px solid #e5e7eb";
        cv.replaceWith(img);
      } catch { cv.remove(); }
    });

    panel.appendChild(clone);

    // Wait for images to render
    await new Promise(res => setTimeout(res, 500));

    // Capture stage as canvas
    const canvas = await html2canvas(stage, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const imgData = canvas.toDataURL("image/png");

    // Create PDF
    const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 24;
    const pdfWidth = pageWidth - 2 * margin;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const ratio = pdfWidth / canvasWidth;
    const scaledHeight = canvasHeight * ratio;

    pdf.addImage(imgData, "PNG", margin, margin, pdfWidth, scaledHeight);
    pdf.save(filename);
    document.body.removeChild(stage);

  } catch (error) {
    console.error("PDF generation failed:", error);
    alert("Failed to create PDF: " + error.message);
    const stage = document.querySelector("div[style*='left: -10000px']");
    if (stage && stage.parentNode) stage.parentNode.removeChild(stage);
  }
}

// ------------------- Image Download -------------------
export function downloadCanvasAsImage(canvas, filename = "chart.png", type = "png") {
  if (!canvas) return;
  const mime = type === "jpeg" ? "image/jpeg" : "image/png";
  const url = canvas.toDataURL(mime, 1.0);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
}

// ------------------- HTML Download -------------------

export function downloadChartAsHTML(containerEl, canvasEl, filename = "chart.html") {
  if (!canvasEl) return;

  const chartInstance = Chart.getChart(canvasEl);
  if (!chartInstance) {
    alert("Chart instance not found");
    return;
  }

  const chartConfig = JSON.stringify(chartInstance.config, null, 2);

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Chart Export</title>
  <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
  <style>
    body { font-family: system-ui, sans-serif; padding: 20px; }
    canvas { max-width: 100%; }
  </style>
</head>
<body>
  <h2>${filename.replace(".html", "")}</h2>
  <canvas id="chart"></canvas>

  <script>
    const config = ${chartConfig};
    const ctx = document.getElementById("chart").getContext("2d");
    new Chart(ctx, config);
  </script>
</body>
</html>
`;

  const blob = new Blob([htmlContent], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}


/*------------------- Download Dropdown -------------------*/
/**
 * Attaches a download dropdown to the chart container.
 * @param {HTMLElement} containerEl - The container element.
 * @param {HTMLCanvasElement} canvasEl - The chart canvas.
 * @param {string} title - Filename prefix.
 * @param {string|null} responseId - (New) The Backend Response ID. If provided, PDF uses API.
 */
export function attachChartDownloadDropdown(
  containerEl,
  canvasEl,
  title = "Chart",
  responseId = null,
  options = {}
) {
  if (!containerEl || !canvasEl) return;
  const canDownload = options?.canDownload !== false;
  const deniedTooltip =
    options?.deniedTooltip ||
    "Download access is denied for your account. If you think this is an error, please contact your admin.";

  // Check if dropdown already exists
  let bar = containerEl.querySelector(".chart-actions");
  if (!bar) {
    bar = document.createElement("div");
    bar.className = "chart-actions";
    bar.style.position = "absolute";
    bar.style.top = "8px";
    bar.style.right = "8px";
    bar.style.zIndex = "10";

    bar.innerHTML = `
      <button class="dl-trigger" title="Download chart">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7,10 12,15 17,10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      </button>
      <div class="dl-menu" hidden>
        <button data-type="png">PNG</button>
        <button data-type="jpg">JPG</button>
        <button data-type="pdf">PDF</button>
        <button data-type="html">HTML</button>
      </div>
    `;
    containerEl.style.position = "relative";
    containerEl.appendChild(bar);
  }

  const trigger = bar.querySelector(".dl-trigger");
  const menu = bar.querySelector(".dl-menu");
  const menuButtons = menu ? Array.from(menu.querySelectorAll("button[data-type]")) : [];

  trigger.disabled = !canDownload;
  trigger.title = canDownload ? "Download chart" : deniedTooltip;
  trigger.style.opacity = canDownload ? "1" : "0.5";
  trigger.style.cursor = canDownload ? "pointer" : "not-allowed";
  menuButtons.forEach((button) => {
    button.disabled = !canDownload;
    button.title = canDownload ? "" : deniedTooltip;
  });

  // Toggle Menu
  trigger.onclick = (e) => {
    if (!canDownload) return;
    e.stopPropagation();
    menu.hidden = !menu.hidden;
  };

  // Handle Menu Clicks
  menu.onclick = async (e) => {
    const btn = e.target.closest("button[data-type]");
    if (!btn) return;
    if (!canDownload) {
      menu.hidden = true;
      return;
    }

    const type = btn.dataset.type;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const filenameStr = `${title.replace(/\s+/g, "_")}_${ts}.${type}`;

    try {
      if (type === "pdf") {
        if (responseId) {
            // NEW: Use Backend API
            await downloadResponseAsPDF(responseId, filenameStr);
        } else {
            // Fallback: Use Client-side Snapshot
            console.warn("No responseId found, falling back to client-side PDF generation.");
            await downloadBubbleAsPDF(containerEl, { filename: filenameStr, title });
        }
      } else if (type === "html") {
        downloadChartAsHTML(containerEl, canvasEl, filenameStr);
      } else {
        downloadCanvasAsImage(canvasEl, filenameStr, type);
      }
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      menu.hidden = true;
    }
  };

  // Close when clicking outside
  document.addEventListener("click", (e) => {
    if (!bar.contains(e.target)) menu.hidden = true;
  });
}
